import React from 'react';
import { MenuItem as MenuItemBase } from '@mui/material';
import Moment from 'moment';

// lodash
import _ from 'lodash';

// components
import { MenuTriggerButton, SimpleMenu } from '../components/Menus/SimpleMenu';
import { ProtectedMenuItem as ProtectedMenuItemBase } from '../console/agencies/inbox/table/ProtectedMenuItem';
import { withShowable } from '../console/_global/lib/withShowable';

// services
import { downloadBinder, downloadQuoteDocuments } from './QuotesService';
import {
  downloadAWSDoc,
  downloadInvoice,
  downloadReceipt,
} from '../policies/PolicyService';

// utils
import { saveBlobFile } from '../utils/appUtils';
import { isLedgerStyleProduct } from '../types';
import useNotifications from '../_lib/notifications/useNotifications';

const MenuItem = withShowable(MenuItemBase);
const ProtectedMenuItem = withShowable(ProtectedMenuItemBase);

export const ClusterBinderOptions = ({ data: binder }) => {
  const {
    handleDownloadBinder,
    handleDownloadApplication,
    handleDownloadReceipt,
    handleDownloadInvoice,
  } = useHandlers(binder);

  return (
    <SimpleMenu id="mangeAgencyBinders" anchor={<MenuTriggerButton />}>
      <MenuItem
        show={!!binder.quoteId && binder.status !== 'RESCIND'}
        onClick={handleDownloadBinder}
      >
        Download Binder
      </MenuItem>
      <MenuItem show={!binder.isPrimePlus} onClick={handleDownloadApplication}>
        Download Application
      </MenuItem>
      <ProtectedMenuItem
        allow={{ policy: 'view' }}
        show={binder.isPaid && !isLedgerStyleProduct(binder.product)}
        disabled={!binder.isDocumentAvailable}
        onClick={handleDownloadReceipt}
      >
        Download Receipt
      </ProtectedMenuItem>

      <ProtectedMenuItem
        allow={{ policy: 'view' }}
        disabled={!binder.isDocumentAvailable}
        onClick={handleDownloadInvoice}
      >
        Download Invoice
      </ProtectedMenuItem>
    </SimpleMenu>
  );
};

export const useHandlers = (binder: any) => {
  const notifications = useNotifications();

  const handleDownloadBinder = React.useCallback(() => {
    const { quoteId, accountId, quoteNumber, companyName, boundOn } = binder;
    downloadBinder(accountId, quoteId, Moment(boundOn).unix())
      .then(({ data }) => downloadAWSDoc(data))
      .then((response) =>
        saveBlobFile(
          response,
          `${companyName}-${quoteNumber}-Cowbell-Binder`,
          '.pdf'
        )
      )
      .catch(() => notifications.error('Unable to download binder'));
  }, [binder, notifications]);

  const handleDownloadApplication = React.useCallback(() => {
    const { quoteId, companyName, accountId, created, product, quoteNumber } =
      binder;
    const isEndorsement = _.get(binder, 'isEndorsement', false);

    downloadQuoteDocuments(
      accountId,
      quoteId,
      Moment(created).unix(),
      isEndorsement,
      product,
      quoteNumber
    )
      .then(({ data }) => downloadAWSDoc(data))
      .then((response) =>
        saveBlobFile(
          response,
          `${companyName}-${quoteNumber}-Cowbell-Application`,
          '.pdf'
        )
      )
      .catch(() => notifications.error('Unable to download application'));
  }, [binder, notifications]);

  const handleDownloadReceipt = React.useCallback(
    (event: any) => {
      event.stopPropagation();
      const {
        accountId,
        id: policyId,
        created,
        policyNumber,
        isEndorsement,
      } = binder;

      downloadReceipt(
        accountId,
        policyId,
        Moment(created).unix(),
        isEndorsement
      )
        .then(({ data: url }) => {
          downloadAWSDoc(url)
            .then((resp) => {
              saveBlobFile(resp, `${policyNumber}-Cowbell-Receipt`, '.pdf');
            })
            .catch(() => notifications.error('Unable to download the Receipt'));
        })
        .catch(() => notifications.error('Unable to download the Receipt'));
    },
    [binder, notifications]
  );

  const handleDownloadInvoice = React.useCallback(
    (event: any) => {
      event.stopPropagation();

      const {
        accountId,
        id: policyId,
        created,
        policyNumber,
        isEndorsement,
      } = binder;

      downloadInvoice(
        accountId,
        policyId,
        Moment(created).unix(),
        true,
        isEndorsement
      )
        .then((response) => {
          const url = _.get(response, 'data');

          downloadAWSDoc(url).then((resp) => {
            saveBlobFile(resp, `${policyNumber}-Cowbell-Invoice`, '.pdf');
          });
        })
        .catch(console.error.bind(console));
    },
    [binder]
  );

  return {
    handleDownloadBinder,
    handleDownloadApplication,
    handleDownloadReceipt,
    handleDownloadInvoice,
  };
};
